<template>
    <div v-cloak>
        <b-card>
            <div class="row">
                <div class="col-md-4">
                    <strong>On the Job Completion Date:</strong> {{ student.on_job_comp_date | formatDate }}
                </div>

                <div class="col-md-4">
                    <strong>Off the Job Completion Date:</strong>
                    <student-date-component
                        :student-id="student.id"
                        :old-value="student.off_job_comp_date"
                        field-name="CompletedCourse"
                        description="Off the Job Completion Date"
                    />
                </div>

                <div class="col-md-4">
                    <strong>Days Enrolled:</strong> {{ daysEnrolled | toDecimal }}
                </div>
            </div>

            <div
                class="row"
                style="min-height: 31px;"
            >
                <div class="col-md-4">
                    <strong>Days Attended:</strong> {{ daysAttended | toDecimal }}
                </div>

                <div class="col-md-4">
                    <strong>Days Absent:</strong> {{ daysAbsent | toDecimal }}
                </div>

                <div class="col-md-4">
                    <strong>Days Completed:</strong> {{ daysCompleted | toDecimal }}
                </div>
            </div>

            <div class="row">
                <div class="col-md-8">
                    <div class="row">
                        <div class="col-md-6">
                            <strong>Exemption Days:</strong> {{ daysExempted | toDecimal }}
                        </div>

                        <div class="col-md-3">
                            <b-btn-group>
                                <b-btn
                                    id="showProgress"
                                    variant="outline-success"
                                    size="sm"
                                >
                                    Student Progress
                                </b-btn>

                                <b-btn
                                    id="showAttendance"
                                    variant="outline-success"
                                    size="sm"
                                >
                                    Student Attendance
                                </b-btn>
                            </b-btn-group>

                            <!-- student attendance popover -->
                            <b-popover
                                target="showAttendance"
                                triggers="hover focus"
                                placement="auto"
                            >
                                <div v-if="student.attendance_grid && student.attendance_grid.length > 0">
                                    <div class="d-sm-none d-md-block">
                                        <div class="row">
                                            <h6 class="col-sm-4 offset-sm-4">
                                                Present
                                            </h6>
                                            <h6 class="col-sm-4">
                                                Absent
                                            </h6>
                                        </div>
                                    </div>
                                    <div
                                        v-for="record in student.attendance_grid"
                                        :key="record.Year"
                                        class="row"
                                    >
                                        <div class="col-sm-4">
                                            <b>
                                                {{ record.Year }}:
                                            </b>
                                        </div>
                                        <div class="col-sm-4">
                                            <b-badge variant="success">
                                                {{ ((Number(record.Present) / (Number(record.Present) + Number(record.Absent))) * 100) | toDecimal }}%
                                            </b-badge>
                                        </div>
                                        <div class="col-sm-4">
                                            <b-badge variant="danger">
                                                {{ ((Number(record.Absent) / (Number(record.Present) + Number(record.Absent))) * 100) | toDecimal }}%
                                            </b-badge>
                                        </div>
                                    </div>
                                </div>
                                <div v-else>
                                    <h6>No Attendance Recorded</h6>
                                </div>
                            </b-popover>

                            <!-- student progress popover -->
                            <b-popover
                                target="showProgress"
                                triggers="hover focus"
                                placement="auto"
                            >
                                <template slot="title">
                                    <strong>Progress:</strong> {{ (daysCompleted - daysAttended) | toDecimal }}
                                </template>

                                <h5>Attendance</h5>
                                <b-progress
                                    :value="Number(daysAbsent)"
                                    :max="Number(daysEnrolled)"
                                    variant="danger"
                                    class="mb-3"
                                />
                                <h5>Completed</h5>
                                <b-progress
                                    :value="Number(daysCompleted)"
                                    :max="Number(daysEnrolled)"
                                    class="mb-3"
                                />
                                <h5>Enrolments</h5>
                                <b-progress
                                    :value="Number(daysEnrolled)"
                                    :max="Number(daysEnrolled)"
                                    variant="success"
                                />
                            </b-popover>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-6">
                            <strong>
                                Remaining attendance hours left for {{ thisYear }}:
                            </strong> {{ student.remaining_hours_year | toDecimal }}
                        </div>

                        <div class="col-md-6">
                            <strong>
                                Remaining hours for Started Competencies to be completed for {{ thisYear }}:
                            </strong> {{ student.remaining_hours_started_competencies | toDecimal }}
                        </div>
                    </div>
                </div>


                <div
                    class="col-md-4"
                    style="border: 1px solid #6c757d; border-radius: 0.2rem;"
                >
                    Practical Elements
                    <div class="row">
                        <div class="col-md-4">
                            <strong>Total:</strong> {{ practicalElements.length }}
                        </div>

                        <div class="col-md-4">
                            <strong>Complete:</strong> {{ completedPractical.length }}
                        </div>

                        <div class="col-md-4">
                            <strong>Incomplete:</strong> {{ incompletedPractical.length }}
                        </div>
                    </div>
                    Knowledge Elements
                    <div class="row">
                        <div class="col-md-4">
                            <strong>Total:</strong> {{ knowledgeElements.length }}
                        </div>

                        <div class="col-md-4">
                            <strong>Complete:</strong> {{ completedKnowledge.length }}
                        </div>

                        <div class="col-md-4">
                            <strong>Incomplete:</strong> {{ incompletedKnowledge.length }}
                        </div>
                    </div>
                </div>
            </div>
        </b-card>
    </div>
</template>

<script>
/* global moment */
import StudentDateComponent from './StudentDateComponent.vue';

export default {
    name: 'StudentStatistics',
    components: {
        StudentDateComponent,
    },
    filters: {
        formatDate: (value) => {
            if (value) {
                return moment(value.date).format('DD/MM/YYYY');
            }
            return 'N/A';
        },
        toDecimal(x) {
            if (x) {
                return Number.parseFloat(x).toFixed(2);
            }
            return 0;
        },
        toWholeNumber(x) {
            if (x) {
                return Math.ceil(x);
            }
            return 0;
        },
    },
    props: {
        student: {
            type: Object,
            required: true,
        },
        enrolments: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            thisYear: moment().year(),
        };
    },
    computed: {
        competencies() {
            return this.enrolments.filter(enrolment => enrolment.code === enrolment.element_code && Number(enrolment.old_course) === 0);
        },
        onlyElements() {
            return this.enrolments.filter(enrolment => enrolment.code !== enrolment.element_code && Number(enrolment.old_course) === 0);
        },
        knowledgeElements() {
            return this.onlyElements.filter(element => Number.isNaN(Number(element.element_code.split('.').pop())));
        },
        completedKnowledge() {
            return this.knowledgeElements.filter(element => element.result.includes('P') || element.result.includes('Ex'));
        },
        incompletedKnowledge() {
            return this.knowledgeElements.filter(element => element.result.includes('E'));
        },
        practicalElements() {
            return this.onlyElements.filter(element => !Number.isNaN(Number(element.element_code.split('.').pop())));
        },
        completedPractical() {
            return this.practicalElements.filter(element => element.result.includes('P') || element.result.includes('Ex'));
        },
        incompletedPractical() {
            return this.practicalElements.filter(element => element.result.includes('E'));
        },
        completedElements() {
            return this.onlyElements.filter(element => element.result.includes('P') || element.result.includes('Ex'));
        },
        exemptedElements() {
            return this.onlyElements.filter(element => element.result.includes('Ex'));
        },
        totalHours() {
            return this.competencies.reduce((prev, cur) => Number(prev) + Number(cur.hours), 0);
        },
        daysAttended() {
            return this.student.attendance_grid.reduce((prev, cur) => Number(prev) + Number(cur.Present), 0);
        },
        daysAbsent() {
            return this.student.attendance_grid.reduce((prev, cur) => Number(prev) + Number(cur.Absent), 0);
        },
        daysEnrolled() {
            //  total number of hours for competencies
            return this.totalHours / 7.6;
        },
        daysCompleted() {
            return this.completedElements.reduce((prev, cur) => Number(prev) + Number(cur.hours), 0) / 7.6;
        },
        daysExempted() {
            return this.exemptedElements.reduce((prev, cur) => Number(prev) + Number(cur.hours), 0) / 7.6;
        },
    },
};
</script>

<style>
    .popover {
        width: 325px !important;
        max-width: 325px !important;
    }
</style>
